import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getRole } from '@client/new/components/AdminMenu/helpers';
import { toAsync } from '@client/new/helpers/reactQuery';
import type { Async, FDispatch } from '@client/new/helpers/types';
import { createDispatch } from '@client/new/helpers/types-utils';

import {
  FeatureFeedbackResponse,
  FeedbackPayload,
  createFeatureFeedback,
  getFeatureFeedback,
} from '../feedback.api';
import { feedbackKeys } from '../feedback.keys';

export type FeedbackEntry = {
  answers: FeedbackPayload;
  createdAt: string;
};

type FeedbackModelActionsMap = { Send: FeedbackPayload };
type FeedbackModelResultMap = { Send: Promise<FeedbackEntry> };

export type FeedbackModel = {
  canSendFeedback: boolean;
  latest: Async<FeedbackEntry | null>;
  dispatch: FDispatch<FeedbackModelActionsMap, FeedbackModelResultMap>;
};

const toEntry = (row: FeatureFeedbackResponse): FeedbackEntry => ({
  answers: row.feedback,
  createdAt: row.createdAt,
});

const toLatest = (rows: FeatureFeedbackResponse[]): FeedbackEntry | null =>
  rows[0] ? toEntry(rows[0]) : null;

export const useFeedbackModel = (): FeedbackModel => {
  const queryClient = useQueryClient();
  const queryKey = feedbackKeys.list;
  const canSendFeedback = getRole() !== 'contentEditor';

  const query = useQuery({
    queryKey,
    queryFn: getFeatureFeedback,
    select: toLatest,
    enabled: canSendFeedback,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: createFeatureFeedback,
    retry: false,
    onSuccess: (created) =>
      queryClient.setQueryData<FeatureFeedbackResponse[]>(queryKey, (rows) => [
        created,
        ...(rows ?? []),
      ]),
  });

  return {
    canSendFeedback,
    latest: toAsync(query, String),
    dispatch: createDispatch<FeedbackModelActionsMap, FeedbackModelResultMap>({
      Send: async (answers) => toEntry(await mutation.mutateAsync(answers)),
    }),
  };
};
